import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import axios from 'axios';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Camera, Loader2, ZoomIn, ZoomOut } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.crossOrigin = 'anonymous';
    image.src = url;
  });

async function getCroppedImg(imageSrc, pixelCrop) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
  });
}

const ProfileImageUpload = ({ user, onUploadComplete, size = 'lg' }) => {
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(null);

  const sizeClass = size === 'lg' ? 'h-32 w-32' : 'h-20 w-20';
  const textSize = size === 'lg' ? 'text-3xl' : 'text-xl';

  // Fetch profile image on mount
  React.useEffect(() => {
    if (user?.profile_image_file_id) {
      axios.get(`${API_URL}/api/files/download/${user.profile_image_file_id}`, {
        responseType: 'blob'
      }).then(res => {
        setAvatarUrl(prev => {
          if (prev) URL.revokeObjectURL(prev);
          return URL.createObjectURL(res.data);
        });
      }).catch(() => {});
    }
  }, [user?.profile_image_file_id]);

  const getInitials = (firstName, lastName) => {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be less than 10MB');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageSrc(reader.result);
      setCropDialogOpen(true);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleUpload = async () => {
    if (!croppedAreaPixels || !imageSrc) return;

    setUploading(true);
    try {
      const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const formData = new FormData();
      formData.append('file', croppedBlob, 'profile.jpg');

      const uploadResp = await axios.post(
        `${API_URL}/api/files/upload/image?category=profiles`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const fileId = uploadResp.data.id;

      await axios.put(`${API_URL}/api/auth/profile`, {
        profile_image_file_id: fileId
      });

      setAvatarUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(croppedBlob);
      });

      setCropDialogOpen(false);
      setImageSrc(null);
      toast.success('Profile photo updated!');

      if (onUploadComplete) onUploadComplete(fileId);
    } catch (error) {
      toast.error('Failed to upload photo');
      console.error(error);
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <div className="relative group" data-testid="profile-image-upload">
        <Avatar className={`${sizeClass} border-4 border-white shadow-lg`}>
          <AvatarImage src={avatarUrl || user?.profile_photo} />
          <AvatarFallback className={`bg-gold text-maroon ${textSize} font-bold`}>
            {getInitials(user?.first_name, user?.last_name)}
          </AvatarFallback>
        </Avatar>
        <label
          className="absolute inset-0 rounded-full flex items-center justify-center bg-black/0 group-hover:bg-black/40 cursor-pointer transition-all"
          data-testid="upload-photo-trigger"
        >
          <Camera className="h-6 w-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
          <input
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
          />
        </label>
      </div>

      <Dialog open={cropDialogOpen} onOpenChange={setCropDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-maroon">Crop Profile Photo</DialogTitle>
          </DialogHeader>
          <div className="relative w-full h-72 bg-gray-900 rounded-lg overflow-hidden">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </div>
          <div className="flex items-center gap-3 px-2">
            <ZoomOut className="h-4 w-4 text-gray-400" />
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-maroon"
            />
            <ZoomIn className="h-4 w-4 text-gray-400" />
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => { setCropDialogOpen(false); setImageSrc(null); }}
              disabled={uploading}
            >
              Cancel
            </Button>
            <Button
              className="bg-maroon hover:bg-maroon-hover text-white"
              onClick={handleUpload}
              disabled={uploading}
              data-testid="save-profile-photo-btn"
            >
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Camera className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading...' : 'Save Photo'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProfileImageUpload;
